/**
 * Runtime overlay for the canonical clinic NAP.
 *
 * `clinicNAP` in `clinic.ts` is the static seed/default. Once the
 * `clinic_settings` row is fetched from Lovable Cloud, every consumer
 * reads from this overlay so edits in the admin settings panel
 * propagate to the visible NAP widgets and JSON-LD without a redeploy.
 *
 * - React components subscribe via `useLiveClinic()`.
 * - Non-React code (seo graph, napAudit) reads the current value
 *   synchronously via `getLiveClinic()` and can listen to `subscribe()`.
 */
import { useSyncExternalStore } from "react";
import { clinicNAP as defaultClinic, type ClinicNAP } from "./clinic";

let current: ClinicNAP = defaultClinic;
const listeners = new Set<() => void>();

export const getLiveClinic = (): ClinicNAP => current;

export const setLiveClinic = (next: ClinicNAP) => {
  current = next;
  listeners.forEach((l) => l());
};

export const subscribeLiveClinic = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const useLiveClinic = (): ClinicNAP =>
  useSyncExternalStore(subscribeLiveClinic, getLiveClinic, getLiveClinic);

// ---------------- DB row → ClinicNAP shape ----------------

export interface ClinicSettingsRow {
  id: string;
  name: string;
  short_name: string;
  alternate_name: string | null;
  legal_name: string | null;
  tagline: string | null;
  street: string;
  locality: string;
  region: string;
  postal_code: string;
  country: string;
  country_code: string;
  latitude: number | null;
  longitude: number | null;
  phone_international: string;
  phone_display: string;
  whatsapp_number: string | null;
  whatsapp_display: string | null;
  whatsapp_url: string | null;
  hours: Array<{
    days: string;
    time: string;
    schema: string[];
    opens: string | null;
    closes: string | null;
  }>;
  booking_note: string | null;
  website_url: string | null;
  website_display: string | null;
  instagram_url: string | null;
  instagram_handle: string | null;
  booking_url: string | null;
  updated_at: string;
}

export const rowToClinic = (row: ClinicSettingsRow): ClinicNAP => ({
  name: row.name,
  legalName: row.legal_name ?? row.name,
  alternateName: row.alternate_name ?? defaultClinic.alternateName,
  shortName: row.short_name,
  tagline: row.tagline ?? defaultClinic.tagline,
  founded: defaultClinic.founded,
  address: {
    street: row.street,
    locality: row.locality,
    region: row.region,
    postalCode: row.postal_code,
    country: row.country,
    countryCode: row.country_code,
  },
  geo: {
    latitude: row.latitude ?? defaultClinic.geo.latitude,
    longitude: row.longitude ?? defaultClinic.geo.longitude,
  },
  phone: {
    international: row.phone_international,
    display: row.phone_display,
  },
  whatsapp: {
    number: row.whatsapp_number ?? defaultClinic.whatsapp.number,
    display: row.whatsapp_display ?? defaultClinic.whatsapp.display,
    url: row.whatsapp_url ?? defaultClinic.whatsapp.url,
  },
  hours: row.hours?.length ? row.hours : defaultClinic.hours,
  bookingNote: row.booking_note ?? defaultClinic.bookingNote,
  social: {
    website: row.website_url ?? defaultClinic.social.website,
    websiteDisplay: row.website_display ?? defaultClinic.social.websiteDisplay,
    instagram: row.instagram_url ?? defaultClinic.social.instagram,
    instagramHandle: row.instagram_handle ?? defaultClinic.social.instagramHandle,
  },
  bookingUrl: row.booking_url ?? defaultClinic.bookingUrl,
});

// ---------------- Derived helpers (always read live) ----------------

export const liveTelHref = () =>
  `tel:${current.phone.international.replace(/\s+/g, "")}`;

export const liveMapsHref = () =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    `${current.name} ${current.address.street} ${current.address.postalCode}`,
  )}`;

export const liveMapsEmbedHref = () =>
  `https://www.google.com/maps?q=${encodeURIComponent(
    `${current.address.street}, ${current.address.locality}, ${current.address.region} ${current.address.postalCode}, ${current.address.country}`,
  )}&output=embed`;

export const liveOneLineAddress = () =>
  [
    current.address.street,
    current.address.locality,
    `${current.address.region} ${current.address.postalCode}`,
    current.address.country,
  ].join(", ");

export const liveSocialUrls = () => [current.social.website, current.social.instagram];
